/*
 *
 * The `CatalogPatch` overview card.
 *
 * One card, deliberately: what a single patch is doing is a handful of facts,
 * and the aggregate view is `/catalog?filters[kind]=catalogpatch`. The patch
 * page is also where a schema error on the patch lands, which is why the page
 * around this card keeps `EntityWarningContent`.
 *
 */

import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Card, CardBody, CardHeader, Text } from '@backstage/ui'
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react'
import { PropsWithChildren } from 'react'
import { Code } from '../typography'
import {
  ANNOTATION_PATCH_OBSERVED,
  readPatchAppliedCount,
  readPatchDetails,
  readPatchStatus,
  readTargetStatuses
} from './catalogPatchAnnotations'
import { PatchStatusIndicator } from './PatchStatusIndicator'

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: 0.5
  },
  field: {
    marginBottom: theme.spacing(2)
  }
}))

function Field(props: PropsWithChildren<{ title: string }>) {
  const classes = useStyles()
  return (
    <Box className={classes.field}>
      <Typography variant="h2" className={classes.label}>
        {props.title}
      </Typography>
      {props.children}
    </Box>
  )
}

function Lines(props: { values: string[] }) {
  return (
    <>
      {props.values.map(value => (
        <Typography variant="body2" component="div" key={value}>
          <Code>{value}</Code>
        </Typography>
      ))}
    </>
  )
}

export function EntityCatalogPatchCard() {
  const { entity } = useEntity()
  const details = readPatchDetails(entity)
  const status = readPatchStatus(entity)
  const targetStatuses = readTargetStatuses(entity)
  const appliedCount = readPatchAppliedCount(entity)
  const observed = entity.metadata.annotations?.[ANNOTATION_PATCH_OBSERVED]

  return (
    <Card>
      <CardHeader>
        <Text variant="title-x-small" weight="bold">
          Catalog Patch
        </Text>
      </CardHeader>
      <CardBody>
        <Field title="Status">
          <PatchStatusIndicator status={status} />
          {appliedCount !== undefined && (
            <Typography variant="body2" component="div">
              currently correcting {appliedCount}{' '}
              {appliedCount === 1 ? 'entity' : 'entities'} whose file still holds the old
              value
            </Typography>
          )}
          {observed && (
            <Typography variant="body2" component="div">
              observed on the target: <Code>{observed}</Code>
            </Typography>
          )}
        </Field>

        <Field title={details.global ? 'Target' : 'Targets'}>
          {details.global ? (
            <Typography variant="body2">
              Every entity in the catalog whose value matches the condition below
            </Typography>
          ) : targetStatuses.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Entity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Observed</TableCell>
                  <TableCell>Authored</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targetStatuses.map(target => (
                  <TableRow key={target.ref}>
                    <TableCell>
                      <EntityRefLink entityRef={target.ref} />
                    </TableCell>
                    <TableCell>
                      <PatchStatusIndicator status={target.status} />
                    </TableCell>
                    <TableCell>
                      <Code>{target.observed ?? '—'}</Code>
                    </TableCell>
                    <TableCell>
                      <Code>{target.from ?? '—'}</Code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            details.targets.map(target => (
              <Typography variant="body2" component="div" key={target}>
                <EntityRefLink entityRef={target} />
              </Typography>
            ))
          )}
        </Field>

        {details.conditions.length > 0 && (
          <Field title="Applies only while">
            <Lines values={details.conditions} />
          </Field>
        )}

        <Field title="Changes">
          <Lines values={details.ops} />
        </Field>

        {details.reason && (
          <Field title="Reason">
            <Typography variant="body2">{details.reason}</Typography>
          </Field>
        )}

        {details.upstream.length > 0 && (
          <Field title="Upstream">
            {details.upstream.map(url => (
              <Typography variant="body2" component="div" key={url}>
                <Link href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </Link>
              </Typography>
            ))}
          </Field>
        )}
      </CardBody>
    </Card>
  )
}
